import { Metric } from 'web-vitals';

/**
 * Performance monitoring utility for RTI Portal
 * Tracks Core Web Vitals and custom performance metrics
 */

interface PerformanceReport {
  name: string;
  value: number;
  delta: number;
  id: string;
}

/**
 * Send performance metrics to analytics endpoint
 * Replace with your actual analytics implementation (Google Analytics, Drupal endpoint, etc.)
 */
function sendToAnalytics(metric: Metric) {
  const report: PerformanceReport = {
    name: metric.name,
    value: metric.value,
    delta: metric.delta,
    id: metric.id,
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Performance Metric:', report);
  }

  // Send to your analytics endpoint
  // Example: Google Analytics 4
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', metric.name, {
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      event_category: 'Web Vitals',
      event_label: metric.id,
      non_interaction: true,
    });
  }

  // Example: Custom Drupal endpoint
  // fetch('/api/analytics/performance', {
  //   method: 'POST',
  //   headers: { 'Content-Type': 'application/json' },
  //   body: JSON.stringify(report),
  // }).catch(console.error);
}

/**
 * Initialize performance monitoring
 * Measures Core Web Vitals: CLS, FID, FCP, LCP, TTFB
 */
export function initPerformanceMonitoring() {
  import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
    // Core Web Vitals
    getCLS(sendToAnalytics); // Cumulative Layout Shift
    getFID(sendToAnalytics); // First Input Delay
    getFCP(sendToAnalytics); // First Contentful Paint
    getLCP(sendToAnalytics); // Largest Contentful Paint
    getTTFB(sendToAnalytics); // Time to First Byte
  });
}

/**
 * Custom performance mark utility
 * Use to measure custom operations (API calls, renders, etc.)
 */
export function performanceMark(name: string) {
  if (typeof performance !== 'undefined' && performance.mark) {
    performance.mark(name);
  }
}

/**
 * Custom performance measure utility
 * Measures time between two marks
 */
export function performanceMeasure(name: string, startMark: string, endMark?: string) {
  if (typeof performance !== 'undefined' && performance.measure) {
    try {
      const measure = performance.measure(name, startMark, endMark);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`⏱️ ${name}: ${measure.duration.toFixed(2)}ms`);
      }

      return measure;
    } catch (error) {
      console.error('Performance measure failed:', error);
    }
  }
}

/**
 * Monitor component render performance
 * Use with React Profiler or manually in useEffect
 */
export function measureComponentRender(componentName: string, duration: number) {
  if (process.env.NODE_ENV === 'development') {
    console.log(`🔄 ${componentName} rendered in ${duration.toFixed(2)}ms`);
  }

  // Track slow renders (>16ms for 60fps)
  if (duration > 16) {
    console.warn(`⚠️ Slow render detected: ${componentName} (${duration.toFixed(2)}ms)`);
  }
}

/**
 * Monitor API call performance
 */
export function measureAPICall(endpoint: string, duration: number, success: boolean) {
  const metric = {
    endpoint,
    duration,
    success,
    timestamp: Date.now(),
  };

  if (process.env.NODE_ENV === 'development') {
    console.log(`🌐 API Call: ${endpoint} - ${duration.toFixed(2)}ms - ${success ? '✅' : '❌'}`);
  }

  // Track slow API calls (>1s)
  if (duration > 1000) {
    console.warn(`⚠️ Slow API call: ${endpoint} (${duration.toFixed(2)}ms)`);
  }

  return metric;
}

/**
 * Get current page load performance metrics
 */
export function getPageLoadMetrics() {
  if (typeof performance === 'undefined' || !performance.timing) {
    return null;
  }

  const timing = performance.timing;
  const metrics = {
    // Network timing
    dns: timing.domainLookupEnd - timing.domainLookupStart,
    tcp: timing.connectEnd - timing.connectStart,
    request: timing.responseStart - timing.requestStart,
    response: timing.responseEnd - timing.responseStart,
    
    // Page load timing
    domInteractive: timing.domInteractive - timing.navigationStart,
    domComplete: timing.domComplete - timing.navigationStart,
    loadComplete: timing.loadEventEnd - timing.navigationStart,
    
    // Total page load
    totalTime: timing.loadEventEnd - timing.navigationStart,
  };

  if (process.env.NODE_ENV === 'development') {
    console.table(metrics);
  }

  return metrics;
}

/**
 * Report long tasks (tasks that block the main thread for >50ms)
 */
export function observeLongTasks() {
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > 50) {
          console.warn(`⚠️ Long Task detected: ${entry.duration.toFixed(2)}ms`, entry);
        }
      }
    });

    observer.observe({ entryTypes: ['longtask'] });
  } catch (error) {
    // Browser doesn't support long task monitoring
  }
}

/**
 * Memory usage monitoring (Chrome only)
 */
export function getMemoryUsage() {
  if (typeof performance !== 'undefined' && (performance as any).memory) {
    const memory = (performance as any).memory;
    return {
      usedJSHeapSize: (memory.usedJSHeapSize / 1048576).toFixed(2) + ' MB',
      totalJSHeapSize: (memory.totalJSHeapSize / 1048576).toFixed(2) + ' MB',
      jsHeapSizeLimit: (memory.jsHeapSizeLimit / 1048576).toFixed(2) + ' MB',
    };
  }
  return null;
}
